
/* eslint-disable max-len */
export const convertToPatientTimeZoneDateFormat = (
  timestamp,
  format,
  timezone
) => {
  // Handle edge cases
  if (timestamp === 0 || timezone === "-" || !timezone) {
    return "-";
  }

  try {
    const date = new Date(timestamp * 1000); // Convert to milliseconds

    // Validate the date
    if (isNaN(date.getTime())) {
      return "-";
    }

    // Use Intl.DateTimeFormat to get date/time parts based on the timezone
    const getTimeInTimeZone = (date, timeZone) => {
      const options = {
        timeZone,
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: false,
      };
      const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(
        date
      );

      const getPart = (type) => parts.find((p) => p.type === type)?.value;

      return {
        day: getPart("day"),
        month: getPart("month"),
        year: getPart("year"),
        hour: getPart("hour"),
        minute: getPart("minute"),
        second: getPart("second"),
      };
    };

    let formattedDate = "";
    const { day, month, year, hour, minute, second } = getTimeInTimeZone(
      date,
      timezone
    );

    // Ensure all values are valid
    if (!day || !month || !year || hour === undefined || !minute || !second) {
      return "-";
    }

    // Utility functions
    const removeLeadingZeros = (value) => parseInt(value, 10).toString();
    const padZero = (value) => value.toString().padStart(2, "0");
    const get12HourTime = (hour24) => {
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const amPm = hour24 >= 12 ? "PM" : "AM";
      return { hour12, amPm };
    };

    switch (format) {
      case "DD/MM/YYYY, 24hrs, No leading zeros":
        formattedDate = `${removeLeadingZeros(day)}/${removeLeadingZeros(
          month
        )}/${year} ${removeLeadingZeros(hour)}:${padZero(minute)}:${padZero(
          second
        )}`;
        break;

      case "MM/DD/YYYY, 24hrs, No leading zeros":
        formattedDate = `${removeLeadingZeros(month)}/${removeLeadingZeros(
          day
        )}/${year} ${removeLeadingZeros(hour)}:${padZero(minute)}:${padZero(
          second
        )}`;
        break;

      case "DD/MM/YYYY, 24hrs, Leading zeros":
        formattedDate = `${padZero(day)}/${padZero(month)}/${year} ${padZero(
          hour
        )}:${padZero(minute)}:${padZero(second)}`;
        break;

      case "MM/DD/YYYY, 24hrs, Leading zeros":
        formattedDate = `${padZero(month)}/${padZero(day)}/${year} ${padZero(
          hour
        )}:${padZero(minute)}:${padZero(second)}`;
        break;

      case "DD/MM/YYYY, AM/PM, No leading zeros": {
        const { hour12, amPm } = get12HourTime(parseInt(hour, 10));
        formattedDate = `${removeLeadingZeros(day)}/${removeLeadingZeros(
          month
        )}/${year} ${hour12}:${padZero(minute)}:${padZero(second)} ${amPm}`;
        break;
      }

      case "MM/DD/YYYY, AM/PM, No leading zeros": {
        const { hour12, amPm } = get12HourTime(parseInt(hour, 10));
        formattedDate = `${removeLeadingZeros(month)}/${removeLeadingZeros(
          day
        )}/${year} ${hour12}:${padZero(minute)}:${padZero(second)} ${amPm}`;
        break;
      }

      case "DD/MM/YYYY, AM/PM, Leading zeros": {
        const { hour12, amPm } = get12HourTime(parseInt(hour, 10));
        formattedDate = `${padZero(day)}/${padZero(month)}/${year} ${padZero(
          hour12
        )}:${padZero(minute)}:${padZero(second)} ${amPm}`;
        break;
      }

      case "MM/DD/YYYY, AM/PM, Leading zeros": {
        const { hour12, amPm } = get12HourTime(parseInt(hour, 10));
        formattedDate = `${padZero(month)}/${padZero(day)}/${year} ${padZero(
          hour12
        )}:${padZero(minute)}:${padZero(second)} ${amPm}`;
        break;
      }

      default:
        // Default format with consistent padding
        formattedDate = `${padZero(day)}/${padZero(month)}/${year} ${padZero(
          hour
        )}:${padZero(minute)}:${padZero(second)}`;
    }

    return formattedDate;
  } catch (error) {
    // Handle invalid timezone or other errors
    console.warn("Error formatting date:", error.message);
    return "-";
  }
};

// Example usage and testing
/*
// Test cases:
const timestamp = 1704110400; // 2024-01-01 12:00:00 UTC

console.log(convertToPatientTimeZoneDateFormat(timestamp, 'DD/MM/YYYY, 24hrs, Leading zeros', 'America/New_York'));
// Expected: 01/01/2024 07:00:00 (EST)

console.log(convertToPatientTimeZoneDateFormat(timestamp, 'MM/DD/YYYY, AM/PM, No leading zeros', 'Asia/Tokyo'));
// Expected: 1/1/2024 9:00:00 PM (JST)

console.log(convertToPatientTimeZoneDateFormat(timestamp, 'DD/MM/YYYY, AM/PM, Leading zeros', 'Europe/London'));
// Expected: 01/01/2024 12:00:00 PM (GMT)

// Edge cases:
console.log(convertToPatientTimeZoneDateFormat(0, 'DD/MM/YYYY, 24hrs, Leading zeros', 'UTC'));
// Expected: -

console.log(convertToPatientTimeZoneDateFormat(timestamp, 'DD/MM/YYYY, 24hrs, Leading zeros', 'Invalid/Timezone'));
// Expected: - (with console warning)
*/
